import React, { useState } from 'react';
import { IFeesAndInclusions, TThemeColor } from '@mayfield/sanity';
import cn from 'classnames';
import {
  Button,
  IntersectionAnimation,
  LayoutProvider
} from '@mayfield/common/components';
import {
  useBreakpoints,
  useIntersectionAnimation
} from '@mayfield/common/hooks';
import * as styles from './styles.module.scss';
import Inclusion from './components/Inclusion';
import Fee from './components/Fee';

interface IProps extends IFeesAndInclusions {
  themeColor: TThemeColor;
  id?: string;
}

const FeesAndInclusions = ({
  fees,
  inclusions,
  feesDisclaimer,
  inclusionsDisclaimer,
  themeColor,
  id
}: IProps) => {
  const { smallDesktop } = useBreakpoints();
  const [showAllInclusionsMobile, setShowAllInclusionsMobile] = useState(false);

  const inclusionsToShow =
    smallDesktop || showAllInclusionsMobile
      ? inclusions
      : inclusions.slice(0, 4);

  const { inView: inViewInclusions, ref: refInclusions } =
    useIntersectionAnimation();
  const { inView: inViewFees, ref: refFees } = useIntersectionAnimation();

  const fallbackFeesDisclaimer =
    'The is an estimate of what you might expect to pay. Fees are based on a 10 hour session fee per day for 3 days, for a specific rate please contact us directly.';

  const fallbackInclusionsDisclaimer =
    'All inclusions are subject to availability and are provided at the discretion of the centre director.';

  return (
    <div className={styles.container} id={id}>
      <LayoutProvider padding grid maxWidth>
        <div className={styles.inclusions}>
          <h2 className={cn('h2', styles.title)}>Inclusions</h2>

          <ul className={styles.inclusionList} ref={refInclusions}>
            {inclusionsToShow.map((inclusion, i) => (
              <li className={styles.inclusion} key={i}>
                <IntersectionAnimation
                  delay={i * 100}
                  trigger={inViewInclusions}
                >
                  <Inclusion inclusion={inclusion} />
                </IntersectionAnimation>
              </li>
            ))}
          </ul>

          {!smallDesktop && (
            <Button
              className={styles.showMoreButton}
              color={themeColor}
              onClick={() => setShowAllInclusionsMobile((prev) => !prev)}
            >
              View {showAllInclusionsMobile ? 'Less' : 'More'}
            </Button>
          )}

          <p className={cn('b2', styles.disclaimer)}>
            {inclusionsDisclaimer || fallbackInclusionsDisclaimer}
          </p>
        </div>

        <div className={styles.fees}>
          <h2 className={cn('h2', styles.title)}>Fees</h2>

          <div className={cn('caption', styles.tableHeadings)}>
            <div>Age</div>
            <div>Rate per day</div>
          </div>

          <ul className={styles.feeItems} ref={refFees}>
            {fees.map((fee, i) => (
              <li key={i}>
                <IntersectionAnimation trigger={inViewFees} delay={100 * i}>
                  <Fee {...fee} />
                </IntersectionAnimation>
              </li>
            ))}
          </ul>

          <p className={cn('b2', styles.disclaimer)}>
            {feesDisclaimer || fallbackFeesDisclaimer}
          </p>
        </div>
      </LayoutProvider>
    </div>
  );
};

export default FeesAndInclusions;
